import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box, TableBody, TableContainer, TableHead, TableRow, TablePagination,
  TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Button,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import recruiterTheme, { COLORS, TYPE, RADIUS, CONTROL_HEIGHT, ELLIPSIS } from '../../styles/recruiterTheme';
import { fetchProctoringSessions, describeApiError } from './proctoringApi';
import {
  ProctoringGlobalStyle, PageWrapper, HeaderBar, BackBtn, PageTitle, PageSubtitle, PageCard,
  StyledTable, HeadCell, BodyRow, BodyCell, StatusPill, ReasonPill, EmptyNote, Muted,
  normalizeSession, roundLabel, fmtClock, fmtDateTime, fmtSeconds, plural, calibrationWord, calibrationColor, useNow,
  readStaffUser, goBackOrDashboard, ROUND_ORDER, STATUS_STYLES, STALE_MS,
} from './proctoringShared';

const ROWS_PER_PAGE = 25;
// One fetch, filtered and paged in the browser. The status and round filters are
// also sent to the API so the payload stays small.
const FETCH_LIMIT = 500;

const STATUS_OPTIONS = Object.keys(STATUS_STYLES).map((key) => ({ value: key, label: STATUS_STYLES[key].label }));
const SERVER_STATUS = { monitoring: 'open', abandoned: 'open', ended: 'ended', superseded: 'ended' };

const fieldSx = {
  minWidth: 168,
  '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px`, background: COLORS.surface, height: CONTROL_HEIGHT.md },
};

const statusTitle = (s) => {
  if (s.statusKey === 'abandoned') return `No camera activity for more than ${Math.round(STALE_MS / 3600000)} hours`;
  if (s.status === 'ended') return s.endedByLabel || undefined;
  return undefined;
};

const ProctoringSessionsView = ({ user }) => {
  const navigate = useNavigate();
  const now = useNow(30000);
  const [statusFilter, setStatusFilter] = useState('');
  const [roundFilter, setRoundFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProctoringSessions({ status: SERVER_STATUS[statusFilter] || '', round: roundFilter, limit: FETCH_LIMIT })
      .then(({ sessions }) => { if (!cancelled) setRawRows(sessions); })
      .catch((err) => {
        if (cancelled) return;
        setRawRows([]);
        setError(describeApiError(err, 'Could not load camera sessions'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter, roundFilter, reloadKey]);

  const sessions = useMemo(
    () => rawRows
      .map((row) => normalizeSession(row, now))
      .sort((a, b) => (Number.isFinite(b.startedMs) ? b.startedMs : 0) - (Number.isFinite(a.startedMs) ? a.startedMs : 0)),
    [rawRows, now],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (statusFilter && s.statusKey !== statusFilter) return false;
      if (roundFilter && s.round !== roundFilter) return false;
      if (q) {
        const hay = `${s.candidatename} ${s.candidateEmail}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sessions, statusFilter, roundFilter, search]);

  const lastPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
  const safePage = Math.min(page, lastPage);
  const pageRows = filtered.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const anyFilter = Boolean(statusFilter || roundFilter || search.trim());

  const openCandidate = (s) => {
    if (s.candidateId) navigate(`/proctoring/${s.candidateId}`);
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2} minHeight="40vh">
          <CircularProgress />
          <Muted>Loading camera sessions…</Muted>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert
          severity="error"
          sx={{ borderRadius: `${RADIUS.md}px` }}
          action={<Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}
        >
          {error}
        </Alert>
      );
    }
    if (filtered.length === 0) {
      return (
        <EmptyNote>
          {anyFilter ? 'No sessions match these filters.' : 'No camera sessions yet. They appear when a candidate starts a round.'}
        </EmptyNote>
      );
    }
    return (
      <>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <StyledTable size="small">
            <TableHead>
              <TableRow>
                <HeadCell $dense>Candidate</HeadCell>
                <HeadCell $dense>Round</HeadCell>
                <HeadCell $dense>Status</HeadCell>
                <HeadCell $dense>Started</HeadCell>
                <HeadCell $dense>Monitored</HeadCell>
                <HeadCell $dense align="center">Episodes</HeadCell>
                <HeadCell $dense>Top reason</HeadCell>
                <HeadCell $dense>Calibrated</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((s, i) => {
                const clickable = Boolean(s.candidateId);
                const name = s.candidatename || 'Unknown candidate';
                const sub = s.candidateEmail || s.candidateId || 'No email on file';
                return (
                  <BodyRow
                    key={s.id || `${s.candidateId}-${s.round}-${s.attempt}-${i}`}
                    onClick={() => openCandidate(s)}
                    onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openCandidate(s); } }}
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? 'link' : undefined}
                    sx={{ cursor: clickable ? 'pointer' : 'default' }}
                    title={clickable ? 'Open camera proctoring for this candidate' : undefined}
                  >
                    <BodyCell $dense sx={{ maxWidth: 220 }}>
                      <Box sx={{ ...ELLIPSIS, fontWeight: 700, color: COLORS.primaryDeep }} title={name}>{name}</Box>
                      <Box sx={{ ...ELLIPSIS, fontSize: TYPE.caption, color: COLORS.muted }} title={sub}>{sub}</Box>
                    </BodyCell>
                    <BodyCell $dense sx={{ whiteSpace: 'nowrap' }}>
                      <Box>{roundLabel(s.round)}</Box>
                      <Muted>Attempt {s.attempt}</Muted>
                    </BodyCell>
                    <BodyCell $dense><StatusPill statusKey={s.statusKey} title={statusTitle(s)} /></BodyCell>
                    <BodyCell $dense sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(s.startedMs)}</BodyCell>
                    <BodyCell $dense sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(s.monitoredS)}</BodyCell>
                    <BodyCell $dense align="center" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      <Box>{s.episodesCount > 0 ? s.episodesCount : <Muted>Quiet</Muted>}</Box>
                      <Muted>{plural(s.warnings, 'warning')}</Muted>
                    </BodyCell>
                    <BodyCell $dense sx={{ whiteSpace: 'nowrap' }}>
                      {s.topReason ? (
                        <>
                          <ReasonPill reason={s.topReason} />
                          {Number.isFinite(s.topReasonS) && s.topReasonS > 0 && (
                            <Box><Muted>{fmtSeconds(s.topReasonS)}</Muted></Box>
                          )}
                        </>
                      ) : <Muted>Quiet</Muted>}
                    </BodyCell>
                    <BodyCell $dense>
                      <span style={{ color: calibrationColor(s), fontWeight: 700 }}>{calibrationWord(s)}</span>
                    </BodyCell>
                  </BodyRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={safePage}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          sx={{ mt: 1, '& .MuiTablePagination-toolbar, & .MuiTablePagination-displayedRows': { fontSize: TYPE.small, color: COLORS.ink } }}
        />
      </>
    );
  };

  return (
    <PageWrapper>
      <ProctoringGlobalStyle />
      <HeaderBar>
        <BackBtn onClick={() => goBackOrDashboard(navigate, user)} aria-label="Back" title="Back">
          <ArrowBackIcon />
        </BackBtn>
        <Box>
          <PageTitle>Camera proctoring</PageTitle>
          <PageSubtitle>One row per round a candidate sat with the camera on.</PageSubtitle>
        </Box>
      </HeaderBar>

      <PageCard elevation={0}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          <TextField
            size="small"
            label="Search name or email"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ ...fieldSx, minWidth: 264 }}
          />
          <FormControl size="small" sx={fieldSx}>
            <InputLabel id="proctoring-status-label">Status</InputLabel>
            <Select
              labelId="proctoring-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={fieldSx}>
            <InputLabel id="proctoring-round-label">Round</InputLabel>
            <Select
              labelId="proctoring-round-label"
              label="Round"
              value={roundFilter}
              onChange={(e) => { setRoundFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All rounds</MenuItem>
              {ROUND_ORDER.map((r) => <MenuItem key={r} value={r}>{roundLabel(r)}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          {!loading && !error && (
            <Muted>
              {anyFilter ? `${filtered.length} of ${sessions.length} sessions` : `${sessions.length} sessions`}
              {sessions.length >= FETCH_LIMIT ? ` (newest ${FETCH_LIMIT})` : ''}
            </Muted>
          )}
        </Box>
        {renderBody()}
      </PageCard>
    </PageWrapper>
  );
};

// Standalone staff route: no saved user means no session, so send them to sign in.
const ProctoringSessionsPage = () => {
  const location = useLocation();
  const user = readStaffUser(location.state);
  if (!user) return <Navigate to="/login" replace />;
  return (
    <ThemeProvider theme={recruiterTheme}>
      <ProctoringSessionsView user={user} />
    </ThemeProvider>
  );
};

export default ProctoringSessionsPage;
